import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import { useEffect } from "react";
import { forceLogoutForExpiredToken } from "../api/utils";

const getTokenExpiryMs = (): number | undefined => {
  const token = Cookies.get("access_token");
  if (!token) return undefined;
  try {
    return jwtDecode<{ exp: number }>(token).exp * 1000;
  } catch {
    // Malformed/undecodable token - the next authenticated request will 401 on it anyway and
    // go through the existing reactive expired_token/forceLogoutForExpiredToken path.
    return undefined;
  }
};

// setTimeout's delay is a 32-bit signed int under the hood - anything past ~24.8 days
// (2^31 - 1 ms) overflows and fires almost immediately instead of waiting that long. This repo's
// own local .env sets authentication_expiry to 123456789 (~3.9 years) for local dev convenience,
// which was overflowing this way and logging local devs out within moments of loading the app
// instead of ~3.9 years later. Capped comfortably under that limit and re-armed in a loop for
// however many chunks a real expiry that far out actually needs.
const MAX_TIMEOUT_MS = 24 * 24 * 60 * 60 * 1000;

// Proactively logs a user out right when their access token expires, instead of leaving them on
// a dead session until they happen to trigger the next API call (the only trigger the existing
// reactive expired_token handler in api/utils.ts has). Mounted once, at the app root (see
// App.tsx) - reading the access_token cookie fresh on mount rather than depending on any auth
// state, since this codebase has no single always-mounted "authenticated shell" otherwise
// (PrivateRoute alone renders once per route).
export const useSessionExpiryLogout = () => {
  useEffect(() => {
    const expiryMs = getTokenExpiryMs();
    // No (or undecodable) token - nothing to time out; PrivateRoute's own auth check already
    // handles getting an unauthenticated user to /login.
    if (!expiryMs) return;

    let timeout: ReturnType<typeof setTimeout>;

    const armNextCheck = () => {
      // Re-check at fire time, not just at schedule time - the user may have logged out
      // through a different path (nav Logout, or the reactive expired_token handler) since this
      // timer was set, in which case there's no token left to expire.
      if (!Cookies.get("access_token")) return;

      const remaining = expiryMs - Date.now();
      if (remaining <= 0) {
        forceLogoutForExpiredToken();
        return;
      }

      timeout = setTimeout(armNextCheck, Math.min(remaining, MAX_TIMEOUT_MS));
    };

    armNextCheck();

    return () => clearTimeout(timeout);
  }, []);
};
